import { useEffect, useRef } from "react";

/**
 * Moves focus into an overlay when it opens, so Tab walks the dialog instead of the
 * page that is still sitting behind the backdrop.
 *
 * Skips when focus is already inside the panel: React applies `autoFocus` during the
 * commit, before this effect runs, so pulling focus back to the shell would undo the
 * field a sheet deliberately focused.
 *
 * Attach the returned ref to the panel element and give it `tabIndex={-1}` plus
 * `focus:outline-none` — it is a focus target, not a tab stop.
 *
 * @param {boolean} isOpen
 * @returns {React.RefObject<HTMLElement>}
 */
export const useDialogFocus = (isOpen) => {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus({ preventScroll: true });
    }
  }, [isOpen]);
  return panelRef;
};

export default useDialogFocus;
